<template>
  <tr class="equity-group-item" :class="{ 'not-usable': !isUsable, 'disable-select-icon': disableSelectIcon }">
    <td class="td-equity-select">
      <DropdownSelect
        v-model="currentEquityId"
        :optionList="selectOptionList"
        :disableSelectIcon="disableSelectIcon"
      ></DropdownSelect>
    </td>
    <td class="td-balance-info">
      <div class="balance-info">
        <span class="balance-text">{{ balanceInfo.text }}</span>
        <span class="balance-suffix">{{ balanceInfo.suffix }}</span>
      </div>
    </td>
    <td class="td-detail-link">
      <router-link class="button-balance-log" :to="detailLink"><span class="button-text">详情</span></router-link>
    </td>
  </tr>
</template>

<script>
import DropdownSelect from '@/components/DropdownSelect.vue'
import { formatDate } from '@/plugins/datefmt'
import { EquityPattern } from '@/plugins/payment'
import _ from 'lodash'

export default {
  components: { DropdownSelect },
  props: {
    equityGroup: {
      type: Object,
      required: true,
    },
    getBalance: {
      type: Function,
      required: true,
    },
    getBalanceDetailLink: {
      type: Function,
      required: true,
    },
    disableSelectIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let equityList = this.equityGroup.equity_s || []
    let currentEquityId = this.chooseDefaultEquityId(equityList)
    return {
      currentEquityId: currentEquityId,
    }
  },
  computed: {
    selectOptionList() {
      if (this.disableSelectIcon) {
        return [
          {
            value: this.currentEquityId,
            name: this.currentEquityName,
          },
        ]
      }
      let equityList = this.equityGroup.equity_s || []
      return equityList.map(equity => {
        return {
          value: equity.id,
          name: equity.name,
        }
      })
    },
    currentEquity() {
      if (_.isNil(this.currentEquityId)) {
        return null
      }
      let equityList = this.equityGroup.equity_s || []
      return equityList.find(x => x.id === this.currentEquityId)
    },
    currentEquityName() {
      return this.currentEquity?.name || ''
    },
    currentBalance() {
      return this.getBalance(this.currentEquityId)
    },
    detailLink() {
      return this.getBalanceDetailLink(this.currentEquityId)
    },
    pattern() {
      return this.currentEquity?.pattern
    },
    balance() {
      return this.currentBalance?.balance
    },
    isInfinite() {
      return this.currentBalance?.is_balance_infinite
    },
    isUsable() {
      return this.currentBalance?.is_balance_usable
    },
    hasCharged() {
      return this.currentBalance?.has_charged
    },
    hasInitial() {
      return this.currentBalance?.has_initial
    },
    isBalanceReady() {
      return !_.isNil(this.balance) && !_.isNil(this.pattern)
    },
    balanceInfo() {
      if (!this.isBalanceReady) {
        return { text: '无', suffix: '' }
      }
      return {
        [EquityPattern.SUBSCRIPTION]: this.subscriptionBalanceInfo,
        [EquityPattern.PROMOTER]: this.subscriptionBalanceInfo,
        [EquityPattern.CONSUMABLE]: this.consumableBalanceInfo,
      }[this.pattern]()
    },
    suffix() {
      if (!this.isBalanceReady) {
        return ''
      }
      let textMap = {
        [EquityPattern.SUBSCRIPTION]: '到期',
        [EquityPattern.PROMOTER]: '到期',
        [EquityPattern.CONSUMABLE]: '积分',
      }
      if (this.isInfinite) {
        textMap = {
          [EquityPattern.SUBSCRIPTION]: '有效',
          [EquityPattern.PROMOTER]: '有效',
          [EquityPattern.CONSUMABLE]: '积分',
        }
      }
      return textMap[this.pattern]
    },
    balanceText() {
      if (!this.isBalanceReady) {
        return ''
      }
      if (this.pattern === EquityPattern.SUBSCRIPTION || this.pattern === EquityPattern.PROMOTER) {
        if (this.isInfinite) {
          return '永久'
        }
        return formatDate(this.balance * 1000)
      } else {
        if (this.isInfinite) {
          return '无限'
        }
        return String(this.balance)
      }
    },
  },
  methods: {
    chooseDefaultEquityId(equityList) {
      let balanceList = []
      equityList.forEach(equity => {
        let balance = this.getBalance(equity.id)
        if (!_.isNil(balance)) {
          balanceList.push(balance)
        }
      })
      let reverseBalanceList = []
      for (let balance of balanceList) {
        reverseBalanceList.unshift(balance)
      }
      for (let balance of reverseBalanceList) {
        if (balance.is_balance_usable) {
          return balance.equity_id
        }
      }
      for (let balance of reverseBalanceList) {
        if (balance.has_charged) {
          return balance.equity_id
        }
      }
      for (let balance of reverseBalanceList) {
        if (balance.has_initial) {
          return balance.equity_id
        }
      }
      if (equityList.length > 0) {
        return equityList[0].id
      }
      return null
    },
    consumableBalanceInfo() {
      if (this.isInfinite) {
        return { text: '无限', suffix: '积分' }
      }
      return { text: String(this.balance), suffix: '积分' }
    },
    subscriptionBalanceInfo() {
      if (this.isInfinite) {
        return { text: '永久有效', suffix: '' }
      }
      const balanceText = formatDate(this.balance * 1000)
      if (this.isUsable) {
        if (!this.hasCharged && this.hasInitial) {
          return { text: balanceText, suffix: '试用到期' }
        } else {
          return { text: balanceText, suffix: '到期' }
        }
      } else {
        if (!this.hasCharged && !this.hasInitial) {
          return { text: '尚未开通', suffix: '' }
        } else {
          return { text: balanceText, suffix: '已过期' }
        }
      }
    },
  },
}
</script>

<style lang="less" scoped>
@import '~@/styles/common';

.equity-group-item {
  td {
    padding-top: 16px;
    vertical-align: middle;
  }
}

.td-balance-info {
  width: 100%;
}

.td-detail-link {
  min-width: 48px;
  text-align: right;
}

.balance-info {
  width: 100%;
  display: flex;
  align-items: center;
}

.button-balance-log {
  display: inline-block;
  padding-top: 4px;
  padding-bottom: 4px;
  color: @antTextGrey;
  display: inline-block;
  text-decoration: none;
  line-height: 1.15;

  .button-text {
    display: inline-block;
    position: relative;
    top: -1px;
    border-bottom: 1px solid lighten(@antTextGrey, 15%);
  }
}

.balance-text {
  padding-right: 8px;
}

.not-usable {
  .balance-text,
  .balance-suffix {
    color: @antGold;
  }
}
</style>
